/**
 * Scene geometry primitives — polygons, rects, spiral search, occupancy grid.
 *
 * Pure, no DOM. Everything is in "scene units": a sheet is 1000 units tall,
 * SHEET_ASPECT * 1000 wide (see sceneSolver). Polygons are arrays of [x, y].
 */

export function pointInPolygon([px, py], poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    const hit = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (hit) inside = !inside;
  }
  return inside;
}

export function polygonBBox(poly) {
  let x1 = Infinity;
  let y1 = Infinity;
  let x2 = -Infinity;
  let y2 = -Infinity;
  for (const [x, y] of poly) {
    if (x < x1) x1 = x;
    if (y < y1) y1 = y;
    if (x > x2) x2 = x;
    if (y > y2) y2 = y;
  }
  return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
}

export function polygonCentroid(poly) {
  let a = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const f = poly[j][0] * poly[i][1] - poly[i][0] * poly[j][1];
    a += f;
    cx += (poly[j][0] + poly[i][0]) * f;
    cy += (poly[j][1] + poly[i][1]) * f;
  }
  if (Math.abs(a) < 1e-9) {
    const b = polygonBBox(poly);
    return [b.x + b.w / 2, b.y + b.h / 2];
  }
  a *= 0.5;
  return [cx / (6 * a), cy / (6 * a)];
}

export function rectPolygon(r) {
  return [
    [r.x, r.y],
    [r.x + r.w, r.y],
    [r.x + r.w, r.y + r.h],
    [r.x, r.y + r.h],
  ];
}

export function rectCenter(r) {
  return [r.x + r.w / 2, r.y + r.h / 2];
}

export function rectsOverlap(a, b, pad = 0) {
  return !(a.x + a.w + pad <= b.x || b.x + b.w + pad <= a.x || a.y + a.h + pad <= b.y || b.y + b.h + pad <= a.y);
}

/** Does segment a→b touch rect r? (Liang–Barsky clip against r's sides.) */
export function segmentHitsRect([ax, ay], [bx, by], r) {
  const dx = bx - ax;
  const dy = by - ay;
  let t0 = 0;
  let t1 = 1;
  for (const [p, q] of [[-dx, ax - r.x], [dx, r.x + r.w - ax], [-dy, ay - r.y], [dy, r.y + r.h - ay]]) {
    if (p === 0) {
      if (q < 0) return false;
      continue;
    }
    const t = q / p;
    if (p < 0) t0 = Math.max(t0, t);
    else t1 = Math.min(t1, t);
    if (t0 > t1) return false;
  }
  return true;
}

export function inflate(r, pad) {
  return { x: r.x - pad, y: r.y - pad, w: r.w + 2 * pad, h: r.h + 2 * pad };
}

/**
 * Fraction of a rect's sample points (center + corners + edge midpoints) that
 * fall inside the polygon. 1 = fully inside.
 */
export function rectInsideness(r, poly) {
  const pts = [
    rectCenter(r),
    [r.x, r.y],
    [r.x + r.w, r.y],
    [r.x, r.y + r.h],
    [r.x + r.w, r.y + r.h],
    [r.x + r.w / 2, r.y],
    [r.x + r.w / 2, r.y + r.h],
    [r.x, r.y + r.h / 2],
    [r.x + r.w, r.y + r.h / 2],
  ];
  let n = 0;
  for (const p of pts) if (pointInPolygon(p, poly)) n++;
  return n / pts.length;
}

/**
 * Archimedean spiral of candidate centers around (cx, cy). `step` is the
 * radial growth per turn; `rng` jitters the angular phase so two searches from
 * the same point don't produce identical candidate orders.
 */
export function spiralCandidates(cx, cy, { step = 40, maxRadius = 1400, perTurn = 10, rng = Math.random } = {}) {
  // Archimedean spiral sampled uniformly by ARC LENGTH: successive candidates
  // are ~`step` apart everywhere, and successive turns are `step` apart
  // radially. (A fixed points-per-turn sampler leaves candidates hundreds of
  // px apart at the outer turns, so it misses pockets a block would fit in.)
  // `perTurn` is kept as a floor for the innermost turn.
  const out = [[cx, cy]];
  const phase = rng() * Math.PI * 2;
  const minDa = (Math.PI * 2) / Math.max(4, perTurn);
  let r = step;
  let a = phase;
  while (r <= maxRadius) {
    out.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
    const da = Math.min(minDa, step / r);
    a += da;
    r += (step * da) / (Math.PI * 2);
  }
  return out;
}

/**
 * Coarse occupancy grid over the sheet. Cells are marked by rects or by an
 * image's alpha grid mapped into a rect. `freeRect` answers whether a rect
 * touches any marked cell (with optional tolerance: the fraction of covered
 * cells allowed).
 */
export class Occupancy {
  constructor(width, height, cols = 96, rows = 54) {
    this.width = width;
    this.height = height;
    this.cols = cols;
    this.rows = rows;
    this.cells = new Uint8Array(cols * rows);
  }

  _cellRange(r) {
    const c0 = Math.max(0, Math.floor((r.x / this.width) * this.cols));
    const c1 = Math.min(this.cols - 1, Math.ceil(((r.x + r.w) / this.width) * this.cols) - 1);
    const r0 = Math.max(0, Math.floor((r.y / this.height) * this.rows));
    const r1 = Math.min(this.rows - 1, Math.ceil(((r.y + r.h) / this.height) * this.rows) - 1);
    return [c0, c1, r0, r1];
  }

  markRect(r, value = 1) {
    const [c0, c1, r0, r1] = this._cellRange(r);
    for (let y = r0; y <= r1; y++) for (let x = c0; x <= c1; x++) this.cells[y * this.cols + x] = value;
  }

  /**
   * Mark from an alpha grid ({cols, rows, data: Uint8Array of 0/1}) that
   * covers `rect` (the full image rect in scene units). `dilate` grows the
   * painted area by that many cells so text keeps a margin from the art.
   */
  markAlpha(alpha, rect, { dilate = 1, value = 1 } = {}) {
    const [c0, c1, r0, r1] = this._cellRange(rect);
    for (let y = r0; y <= r1; y++) {
      for (let x = c0; x <= c1; x++) {
        // Scene cell center → image-normalized coords → alpha cell.
        const sx = ((x + 0.5) / this.cols) * this.width;
        const sy = ((y + 0.5) / this.rows) * this.height;
        const u = (sx - rect.x) / rect.w;
        const v = (sy - rect.y) / rect.h;
        if (u < 0 || u >= 1 || v < 0 || v >= 1) continue;
        const ax = Math.floor(u * alpha.cols);
        const ay = Math.floor(v * alpha.rows);
        if (alpha.data[ay * alpha.cols + ax]) {
          for (let dy = -dilate; dy <= dilate; dy++) {
            for (let dx = -dilate; dx <= dilate; dx++) {
              const xx = x + dx;
              const yy = y + dy;
              if (xx < 0 || yy < 0 || xx >= this.cols || yy >= this.rows) continue;
              this.cells[yy * this.cols + xx] = value;
            }
          }
        }
      }
    }
  }

  /**
   * One pass over the rect's cells: [busy, of] — the fraction occupied by
   * anything OTHER than `value`, and the fraction holding exactly `value`.
   * (The solver asks both of every candidate: "is it free of text / dense
   * paint?" and "how much anchor wash is under it?")
   */
  coverages(r, value = 0) {
    const [c0, c1, r0, r1] = this._cellRange(r);
    let n = 0;
    let busy = 0;
    let of = 0;
    for (let y = r0; y <= r1; y++) {
      for (let x = c0; x <= c1; x++) {
        n++;
        const v = this.cells[y * this.cols + x];
        if (v === value) of++;
        else if (v) busy++;
      }
    }
    return n ? [busy / n, of / n] : [0, 0];
  }

  /** Fraction of the rect's cells that are occupied, ignoring `ignoreValue` (0 = fully free). */
  coverage(r, ignoreValue = 0) {
    return this.coverages(r, ignoreValue)[0];
  }

  freeRect(r, tolerance = 0, ignoreValue = 0) {
    return this.coverage(r, ignoreValue) <= tolerance;
  }

  /** Fraction of the rect's cells holding exactly `value`. */
  coverageOf(r, value) {
    return this.coverages(r, value)[1];
  }
}
